import React, { useState } from "react";
import { Share2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { formatApiDetail } from "../lib/api";
import { shareNoteWithClient } from "../lib/notesApi";

function firstName(name) {
  const cleaned = (name || "").trim();
  if (!cleaned) return "client";
  return cleaned.split(/\s+/)[0];
}

export default function NoteShareDialog({
  note,
  clientContactName = "",
  clientQuery = "",
  isUpdate = false,
  open,
  onOpenChange,
  onBeforeSend,
  onShared,
}) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleOpenChange = (next) => {
    if (!next) setError("");
    onOpenChange(next);
  };

  const handleSend = async () => {
    if (!note?.id || sending) return;
    setSending(true);
    setError("");
    try {
      await onBeforeSend?.();
      const data = await shareNoteWithClient(note.id, clientQuery);
      onShared?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiDetail(e.response?.data?.detail, isUpdate ? "Could not update note." : "Could not share note."));
    } finally {
      setSending(false);
    }
  };

  if (!note) return null;

  const shareTarget = firstName(clientContactName);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)] gap-0 overflow-hidden rounded-[var(--t-event-pill-radius)] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 text-left">
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Share2 size={18} strokeWidth={1.4} />
            {isUpdate ? `Update ${shareTarget}` : `Share with ${shareTarget}`}
          </DialogTitle>
          <DialogDescription className="text-left text-sm leading-relaxed">
            {isUpdate
              ? "They\u2019ll receive an in-app notification."
              : "They\u2019ll receive an email and an in-app notification."}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p className="px-6 pb-2 text-sm text-[var(--t-error)]">{error}</p>
        ) : null}

        <div className="flex flex-col-reverse sm:flex-row gap-2 px-6 py-4 border-t border-[var(--t-border)]">
          <Button
            type="button"
            variant="outline"
            className="!rounded-[var(--t-event-pill-radius)] flex-1"
            onClick={() => handleOpenChange(false)}
            disabled={sending}
          >
            Close
          </Button>
          <Button
            type="button"
            className="!rounded-[var(--t-event-pill-radius)] flex-1"
            disabled={sending}
            onClick={handleSend}
            data-testid="note-share-send"
          >
            {sending ? (isUpdate ? "Updating…" : "Sharing…") : isUpdate ? "Update" : "Share"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
